import type {Component, ComponentDefinition, Editor} from 'grapesjs';
import {noop} from '@Utils/general.util';
import {getUniqueId} from '@Utils/string.util';
import {EmailCampaignAiRewriteSource} from '@Libraries/ai-email-campaign-content-library';
import type {SelectedPromptOptionValues} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import {EMAIL_MAKER_COMMANDS} from '@EmailMakerReact/email-maker-react.constants';
import type {
  BasicComponentCommandProps,
  EmailMakerBannerOptions,
  EmailOverlayStyle,
  HideMediaComponentOverlayOptions,
  ShowMediaComponentOverlayOptions,
} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import type {
  AiRedesignEmailCampaignOptions,
  AiRegenerateEmailCampaignImagesOptions,
  AiRegenerateEmailTemplateDesignsOptions,
  AiRewriteEmailCampaignTextOptions,
} from '@EmailMakerReact/lib/email-maker-ai-library';
import {
  aiRedesignEmailCampaign,
  aiRegenerateEmailCampaignImages,
  aiRegenerateEmailTemplateDesigns,
  aiRegenerateEmailTemplateImages,
  aiRewriteEmailCampaignTextComponents,
  toggleAiEdit,
  toSelectedPromptOptionValues,
} from '@EmailMakerReact/lib/email-maker-ai-library';
import {closeAddBlocksDialog, openAddBlocksDialog} from '@EmailMakerReact/lib/email-maker-block-insert-library';
import {copyComponentStyles, copySelectedComponents, pasteComponents, pasteComponentStyles, removeSelectedComponents} from '@EmailMakerReact/lib/email-maker-clipboard-library';
import {
  fixComponentFullWidthOverflowAolYahoo,
  removeBuggyBackgroundAttribute,
  removeUnsupportedStyles,
  updateImageAlignmentCompatibility,
} from '@EmailMakerReact/lib/email-maker-compatibility-library';
import {getAllComponentsByType, getMainWrapper, getTableComponentForButton, isComponentInsideComponent} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {updateContentMaxWidth} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {openDesignSelectorAndInsert, openPostermakerForDesign} from '@EmailMakerReact/lib/email-maker-design-library';
import type {OpenDesignSelectorAndInsertOptions, OpenPostermakerForDesignOptions} from '@EmailMakerReact/lib/email-maker-design-library';
import {getInlinedHtml} from '@EmailMakerReact/lib/email-maker-html-export-library';
import type {SetImageLoadingOptions, SetImageSrcOptions} from '@EmailMakerReact/lib/email-maker-media-library';
import {setImageLoading, setImageSrc} from '@EmailMakerReact/lib/email-maker-media-library';
import {
  openAddImageOptionSelector,
  openAddVideoOptionSelector,
  openAiImages,
  openExploreMediaImage,
  openIconsModal,
  openMyUploadsImage,
  openStickerModal,
} from '@EmailMakerReact/lib/email-maker-media-modals-library';
import type {DeviceMediaQueryOptions, SectionResponsiveOptions} from '@EmailMakerReact/lib/email-maker-media-query-library';
import {refreshMediaQueries, setDeviceMediaQuery, updateSectionResponsive} from '@EmailMakerReact/lib/email-maker-media-query-library';
import {trackGaEventForComponent} from '@EmailMakerReact/lib/email-maker-misc-library';
import {hideMediaComponentOverlay, showMediaComponentOverlay, toggleComponentLoadingOverlay, toggleLoadingOverlayForEmail} from '@EmailMakerReact/lib/email-maker-overlay-library';
import type {AuthenticateUserOptions} from '@EmailMakerReact/lib/email-maker-publish-library';
import {authenticateUser} from '@EmailMakerReact/lib/email-maker-publish-library';
import type {ScrollToComponentOptions} from '@EmailMakerReact/lib/email-maker-selection-library';
import {refreshSelection, scrollToComponent} from '@EmailMakerReact/lib/email-maker-selection-library';
import {closeEmbeddedEmailEditor, saveAndCloseEmbeddedEditor} from '@EmailMakerReact/lib/email-maker-embedded-editor-library';
import {saveEmailTemplateToDbAndCache} from '@EmailMakerReact/lib/email-maker-storage-library';
import {
  addCopyTableStyleToTableBodyListener,
  getSectionColumnsPresets,
  handleTablePadding,
  syncTableTbodyColors,
  updateColumnsPreset,
} from '@EmailMakerReact/lib/email-maker-table-library';
import type {SectionColumnsPreset} from '@EmailMakerReact/lib/email-maker-table-library';
import type {ApplyGlobalCellStylesOptions} from '@EmailMakerReact/lib/email-maker-table-style-library';
import {applyGlobalCellStyles} from '@EmailMakerReact/lib/email-maker-table-style-library';

/** Editor event fired when the banner wrappers change the banner state; subscribers read {@link getEmailMakerBanner}. */
export const EMAIL_MAKER_BANNER_EVENT = 'email-maker:banner';
/** Model key backing {@link getEmailMakerBanner}. */
const BANNER_STATE_MODEL_KEY = 'pmwEmailMakerBannerState';

interface TypedComponentsCommandOpts {
  types: string[];
  component?: Component;
}

interface ComponentNestingCommandOpts {
  child: Component;
  parent: Component;
}

interface TableComponentForButtonCommandOpts {
  text?: string;
}

interface CopyTableStyleCommandOpts {
  component?: Component;
  stylesToCopy: string[];
}

interface ColumnsPresetCommandOpts {
  component?: Component;
  presetID: number;
}

interface ComponentLoadingOverlayCommandOpts {
  component?: Component;
  show: boolean;
}

interface LoadingOverlayForEmailCommandOpts {
  show: boolean;
  style?: EmailOverlayStyle;
}

interface TrackGaEventCommandOpts {
  component?: Component;
  eventAction: string;
}

interface RefreshMediaQueriesCommandOpts {
  component?: Component;
  responsiveStyles: string[];
}

interface ToggleAiEditCommandOpts {
  enable: boolean;
}

interface CloseEmbeddedEditorCommandOpts {
  isSilentClose: boolean;
}

interface AiRegenerateTemplateImagesCommandOpts {
  prompt: string;
}

/** The two invoker shapes of AI_REWRITE; the `selectedValues` key is the structural discriminant (prompt-box shape only). */
type AiRewriteCommandOpts = AiRewriteEmailCampaignTextOptions | AiRewritePromptBoxCommandOpts;

interface AiRewritePromptBoxCommandOpts {
  prompt: string;
  selectedValues: Partial<SelectedPromptOptionValues> | undefined;
}

export function getEmailMakerBanner(editor: Editor): EmailMakerBannerOptions | null {
  return (editor.getModel().get(BANNER_STATE_MODEL_KEY) as EmailMakerBannerOptions | undefined) ?? null;
}

export function initCommands(editor: Editor): void {
  const cmds = editor.Commands;

  cmds.add(EMAIL_MAKER_COMMANDS.GET_MAIN_WRAPPER, (): Component | undefined => {
    return getMainWrapper(editor);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.GET_ALL_COMPONENTS_BY_TYPE, (_editor: Editor, _sender: unknown, opts: TypedComponentsCommandOpts): Component[] => {
    return getAllComponentsByType(editor, opts.types, opts.component);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.IS_COMPONENT_INSIDE_COMPONENT, (_editor: Editor, _sender: unknown, opts: ComponentNestingCommandOpts): boolean => {
    return isComponentInsideComponent(opts.child, opts.parent);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.GET_TABLE_COMPONENT_FOR_BUTTON, (_editor: Editor, _sender: unknown, opts: TableComponentForButtonCommandOpts): ComponentDefinition => {
    return getTableComponentForButton(opts.text);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.ADD_COPY_TABLE_STYLE_TO_TABLE_BODY_LISTENER, (_editor: Editor, _sender: unknown, opts: CopyTableStyleCommandOpts): void => {
    addCopyTableStyleToTableBodyListener(opts.component ?? editor.getSelected(), opts.stylesToCopy);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.APPLY_GLOBAL_CELL_STYLES, (_editor: Editor, _sender: unknown, opts: ApplyGlobalCellStylesOptions): void => {
    applyGlobalCellStyles(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.HANDLE_TABLE_PADDING, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    handleTablePadding(editor, opts.component ?? editor.getSelected());
  });

  cmds.add(EMAIL_MAKER_COMMANDS.SYNC_TABLE_TBODY_COLORS, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    syncTableTbodyColors(opts.component ?? editor.getSelected());
  });

  cmds.add(EMAIL_MAKER_COMMANDS.UPDATE_COLUMNS_PRESET, (_editor: Editor, _sender: unknown, opts: ColumnsPresetCommandOpts): void => {
    updateColumnsPreset(editor, opts.presetID, opts.component);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.UPDATE_CONTENT_MAX_WIDTH, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    updateContentMaxWidth(editor, opts.component ?? editor.getSelected());
  });

  cmds.add(EMAIL_MAKER_COMMANDS.UPDATE_SECTION_RESPONSIVE, (_editor: Editor, _sender: unknown, opts: SectionResponsiveOptions): void => {
    updateSectionResponsive(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.SET_DEVICE_MEDIA_QUERY, (_editor: Editor, _sender: unknown, opts: DeviceMediaQueryOptions): void => {
    setDeviceMediaQuery(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.REMOVE_UNSUPPORTED_STYLES, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    removeUnsupportedStyles(opts.component ?? editor.getSelected());
  });

  cmds.add(EMAIL_MAKER_COMMANDS.REMOVE_BUGGY_BACKGROUND_ATTRIBUTE, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    removeBuggyBackgroundAttribute(opts.component ?? editor.getSelected());
  });

  cmds.add(EMAIL_MAKER_COMMANDS.UPDATE_IMAGE_ALIGNMENT_COMPATIBILITY, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    updateImageAlignmentCompatibility(editor, opts.component);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.FIX_COMPONENT_FULL_WIDTH_OVERFLOW_AOL_YAHOO, (): void => {
    fixComponentFullWidthOverflowAolYahoo(editor);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.SET_IMAGE_SRC, (_editor: Editor, _sender: unknown, opts: SetImageSrcOptions): Promise<void> => {
    return setImageSrc(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.SET_IMAGE_LOADING, (_editor: Editor, _sender: unknown, opts: SetImageLoadingOptions): void => {
    setImageLoading(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.SHOW_MEDIA_COMPONENT_OVERLAY, (_editor: Editor, _sender: unknown, opts: ShowMediaComponentOverlayOptions): void => {
    showMediaComponentOverlay(editor, opts.element, opts.variant);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.HIDE_MEDIA_COMPONENT_OVERLAY, (_editor: Editor, _sender: unknown, opts: HideMediaComponentOverlayOptions): void => {
    hideMediaComponentOverlay(editor, opts.element);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.TRACK_GA_EVENT_COMPONENT, (_editor: Editor, _sender: unknown, opts: TrackGaEventCommandOpts): void => {
    trackGaEventForComponent(editor, opts.eventAction, opts.component);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.AUTHENTICATE_USER, (_editor: Editor, _sender: unknown, opts: AuthenticateUserOptions): void => {
    authenticateUser(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.OPEN_DESIGN_SELECTOR, (_editor: Editor, _sender: unknown, opts: OpenDesignSelectorAndInsertOptions): void => {
    openDesignSelectorAndInsert(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.OPEN_POSTERMAKER, (_editor: Editor, _sender: unknown, opts: OpenPostermakerForDesignOptions): void => {
    openPostermakerForDesign(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.OPEN_STICKER_MODAL, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    openStickerModal(editor, opts.component);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.OPEN_ICONS_MODAL, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    openIconsModal(editor, opts.component);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.mediaModals.OPEN_ADD_IMAGE_OPTION_SELECTOR, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    openAddImageOptionSelector(editor, opts.component);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.mediaModals.OPEN_MY_UPLOADS_IMAGE, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    openMyUploadsImage(editor, opts.component);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.mediaModals.OPEN_EXPLORE_MEDIA_IMAGE, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    openExploreMediaImage(editor, opts.component);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.mediaModals.OPEN_AI_IMAGES, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    openAiImages(editor, opts.component);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.mediaModals.OPEN_ADD_VIDEO_OPTION_SELECTOR, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    openAddVideoOptionSelector(editor, opts.component);
  });

  // No-op: components.ts invokes this by name, but the slider control is now a controlled input.
  cmds.add(EMAIL_MAKER_COMMANDS.UPDATE_SLIDER_VALUE, noop);

  cmds.add(EMAIL_MAKER_COMMANDS.GET_INLINED_HTML, (): string => {
    return getInlinedHtml(editor);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.GET_SECTION_COLUMNS_PRESETS, (): SectionColumnsPreset[] => {
    return getSectionColumnsPresets();
  });

  cmds.add(EMAIL_MAKER_COMMANDS.SCROLL_TO_COMPONENT, (_editor: Editor, _sender: unknown, opts: ScrollToComponentOptions): void => {
    scrollToComponent(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.REFRESH_STYLEMANAGER, (): void => {
    refreshSelection(editor);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.REFRESH_MEDIA_QUERIES, (_editor: Editor, _sender: unknown, opts: RefreshMediaQueriesCommandOpts): void => {
    refreshMediaQueries(editor, opts.component ?? editor.getSelected(), opts.responsiveStyles);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.COPY_STYLES, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    copyComponentStyles(editor, opts.component);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.PASTE_STYLES, (_editor: Editor, _sender: unknown, opts: BasicComponentCommandProps): void => {
    pasteComponentStyles(editor, opts.component);
  });

  // Ignores opts.component by design and always removes the current selection.
  cmds.add(EMAIL_MAKER_COMMANDS.REMOVE_COMPONENT, (): void => {
    removeSelectedComponents(editor);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.SAVE_TO_DB_AND_CACHE, (): Promise<void> => {
    return saveEmailTemplateToDbAndCache(editor);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.CLOSE_EMBEDDED_EDITOR, (_editor: Editor, _sender: unknown, opts: CloseEmbeddedEditorCommandOpts): void => {
    closeEmbeddedEmailEditor(opts.isSilentClose);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.SAVE_AND_CLOSE_EMBEDDED_EDITOR, (): Promise<void> => {
    return saveAndCloseEmbeddedEditor(editor);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.OPEN_ADD_BLOCKS_DIALOG, (): void => {
    openAddBlocksDialog(editor);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.CLOSE_ADD_BLOCKS_DIALOG, (): void => {
    closeAddBlocksDialog();
  });

  cmds.add(EMAIL_MAKER_COMMANDS.TOGGLE_AI_EDIT, (_editor: Editor, _sender: unknown, opts: ToggleAiEditCommandOpts): void => {
    toggleAiEdit(editor, opts.enable);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, (_editor: Editor, _sender: unknown, opts: LoadingOverlayForEmailCommandOpts): void => {
    toggleLoadingOverlayForEmail(editor, opts.show, opts.style);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.TOGGLE_COMPONENT_LOADING_OVERLAY, (_editor: Editor, _sender: unknown, opts: ComponentLoadingOverlayCommandOpts): void => {
    if (opts.component) {
      toggleComponentLoadingOverlay(editor, opts.component, opts.show);
    }
  });

  cmds.add(EMAIL_MAKER_COMMANDS.AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT, (_editor: Editor, _sender: unknown, opts: AiRewriteCommandOpts): Promise<void> => {
    return aiRewriteEmailCampaignTextComponents(editor, normalizeAiRewriteOptions(opts));
  });

  cmds.add(EMAIL_MAKER_COMMANDS.AI_REDESIGN_EMAIL_CAMPAIGN, (_editor: Editor, _sender: unknown, opts: AiRedesignEmailCampaignOptions): Promise<void> => {
    return aiRedesignEmailCampaign(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.AI_REGENERATE_EMAIL_CAMPAIGN_IMAGES, (_editor: Editor, _sender: unknown, opts: AiRegenerateEmailCampaignImagesOptions): Promise<number> => {
    return aiRegenerateEmailCampaignImages(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.AI_REGENERATE_EMAIL_TEMPLATE_DESIGNS, (_editor: Editor, _sender: unknown, opts: AiRegenerateEmailTemplateDesignsOptions): Promise<void> => {
    return aiRegenerateEmailTemplateDesigns(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.AI_REGENERATE_EMAIL_TEMPLATE_IMAGES, (_editor: Editor, _sender: unknown, opts: AiRegenerateTemplateImagesCommandOpts): Promise<number> => {
    return aiRegenerateEmailTemplateImages(editor, opts.prompt);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.SHOW_BANNER, (_editor: Editor, _sender: unknown, opts: EmailMakerBannerOptions): void => {
    setEmailMakerBanner(editor, opts);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.HIDE_BANNER, (): void => {
    setEmailMakerBanner(editor, null);
  });

  // Empty override that disables GrapesJS's default core:paste (GrapesJS core invokes it by name). Do not remove.
  cmds.add('core:paste', noop);

  cmds.add(EMAIL_MAKER_COMMANDS.CORE_COMPONENT_COPY, (): void => {
    copySelectedComponents(editor);
  });

  cmds.add(EMAIL_MAKER_COMMANDS.CORE_COMPONENT_PASTE, (): void => {
    pasteComponents(editor);
  });
}

function setEmailMakerBanner(editor: Editor, banner: EmailMakerBannerOptions | null): void {
  editor.getModel().set(BANNER_STATE_MODEL_KEY, banner);
  editor.trigger(EMAIL_MAKER_BANNER_EVENT, banner);
}

/** Maps either AI_REWRITE invoker shape onto the lib's options, discriminating on the prompt-box-only `selectedValues` key. */
function normalizeAiRewriteOptions(opts: AiRewriteCommandOpts): AiRewriteEmailCampaignTextOptions {
  if ('selectedValues' in opts) {
    return {
      prompt: opts.prompt,
      selectedPromptOptionValues: toSelectedPromptOptionValues(opts.selectedValues ?? {}),
      usageSource: EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REWRITE,
      contentKey: getUniqueId(),
    };
  }

  return opts;
}
